import { toNumber } from '../../../utils/numberUtils.js'
import { toYearMonth, monthDiff } from '../../../utils/dateUtils.js'

function toYm(value) {
  // One-off Date-fallback wrapper needed for fechaFallecimiento ISO strings (e.g. '2024-03-15')
  // that don't match strict MM/YYYY or YYYY-MM patterns.
  // Deferred to Phase 4 — keeping inline for now.
  const txt = String(value || '')
  const iso = txt.match(/^(\d{4})-(\d{2})/)
  if (iso) return Number(`${iso[1]}${iso[2]}`)
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return Number(`${slash[2]}${slash[1]}`)
  const d = new Date(txt)
  if (Number.isNaN(d.getTime())) return 0
  return Number(`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`)
}

function resolveTasaSustitucion(concurrentes = {}) {
  const hijos = Math.max(0, Number(concurrentes?.hijosCount || 0))
  const conyuge = concurrentes?.conyuge === false ? 0 : 1
  if (!conyuge && hijos <= 0) return 0
  if (!conyuge) return Math.min(1, hijos * 0.2)
  if (hijos <= 0) return 0.7
  return Math.min(1, 0.5 + hijos * 0.2)
}

export function evaluarRegularidadPension(rows = [], fechaFallecimiento = '') {
  const falleYm = toYm(fechaFallecimiento)
  const withAporte = (Array.isArray(rows) ? rows : []).filter((r) => Number(r.monto || r.remImpSS || r.montoOriginal || 0) > 0)
  const totalMesesAporte = withAporte.length
  const m36 = withAporte.filter((r) => {
    const ym = toYm(r.periodoKey || r.periodo || r.periodoISO)
    const delta = monthDiff(falleYm, ym)
    return falleYm && ym && delta >= 0 && delta < 36
  }).length
  const m60 = withAporte.filter((r) => {
    const ym = toYm(r.periodoKey || r.periodo || r.periodoISO)
    const delta = monthDiff(falleYm, ym)
    return falleYm && ym && delta >= 0 && delta < 60
  }).length
  const aniosAporte = totalMesesAporte / 12

  if (m36 >= 30 || aniosAporte >= 30) {
    return { categoria: 'REGULAR', derecho: true, factorQuita: 1, m36, m60, aniosAporte }
  }
  if (m36 >= 12 || (aniosAporte >= 15 && m60 >= 12)) {
    return { categoria: 'IRREGULAR_CON_DERECHO', derecho: true, factorQuita: 0.7, m36, m60, aniosAporte }
  }
  return { categoria: 'SIN_DERECHO', derecho: false, factorQuita: 0, m36, m60, aniosAporte }
}

export function calcularHaberPension(expediente = {}, rows = [], rulesConfig = {}) {
  const datos = expediente?.datosPension || {}
  const tipoPension = String(datos?.tipoPension || '').toUpperCase()
  const concurrentes = datos?.concurrentes || {}
  const tasaSustitucion = resolveTasaSustitucion({
    ...concurrentes,
    conyuge: datos?.concurreConyugeConviviente !== false,
  })

  if (tipoPension === 'DERIVADA') {
    const baseCalculo = toNumber(datos?.haberJubiladoCausante)
    return {
      derechoCausado: baseCalculo > 0 && tasaSustitucion > 0,
      tipoPension: 'DERIVADA',
      haberInicialPension: Number((baseCalculo * tasaSustitucion).toFixed(2)),
      tasaSustitucionAplicada: Number(tasaSustitucion.toFixed(4)),
      baseCalculo,
      explicacionJuridica: 'Pension derivada de jubilado. Art. 97 Ley 24.241 sobre ultimo haber percibido.',
    }
  }

  const falleYm = toYm(datos?.fechaFallecimiento)
  const windowRows = (Array.isArray(rows) ? rows : [])
    .filter((r) => Number(r.montoActualizado || r.montoSujetoAAporte || r.monto || 0) > 0)
    .filter((r) => {
      const ym = toYm(r.periodoKey || r.periodo || r.periodoISO)
      const delta = monthDiff(falleYm, ym)
      return falleYm && ym && delta >= 0 && delta < 120
    })
    .sort((a, b) => String(b.periodoKey || '').localeCompare(String(a.periodoKey || '')))
    .slice(0, 60)

  const ingresoBase = windowRows.length
    ? windowRows.reduce((acc, r) => acc + toNumber(r.montoActualizado || r.montoSujetoAAporte || r.monto), 0) / Math.max(1, windowRows.length)
    : 0
  const regularidad = evaluarRegularidadPension(rows, datos?.fechaFallecimiento)
  const irregularFactor = Number(
    rulesConfig?.scenarioApplied?.tasaSustitucionIrregular ||
    rulesConfig?.scenarioMatrix?.PENSION_DIRECTA?.tasaSustitucionIrregular ||
    0.7,
  )
  const baseConRegularidad = ingresoBase * regularidad.factorQuita
  const baseAjustada = regularidad.categoria === 'IRREGULAR_CON_DERECHO'
    ? (ingresoBase * irregularFactor)
    : baseConRegularidad
  return {
    derechoCausado: regularidad.derecho && baseConRegularidad > 0 && tasaSustitucion > 0,
    tipoPension: 'DIRECTA',
    categoriaAportante: regularidad.categoria,
    regularidad,
    ingresoBase: Number(ingresoBase.toFixed(2)),
    haberInicialPension: Number((baseAjustada * tasaSustitucion).toFixed(2)),
    tasaSustitucionAplicada: Number(tasaSustitucion.toFixed(4)),
    tasaSustitucionIrregularAplicada: irregularFactor,
    explicacionJuridica: 'Pension directa: analisis de regularidad Dec. 460/99 + sustitucion Art. 97 Ley 24.241.',
  }
}
